/**
 * The reports API requires a full ISO datetime with an offset — the shared
 * contract is `z.string().datetime({ offset: true })` (see
 * `@equinox/shared`'s `isoDateTime`). The report screens only collect a bare
 * `YYYY-MM-DD` from `<input type="date">` (see `lib/format.ts`'s
 * `todayISO`/`daysAgoISO`/`startOfMonthISO`), so every report call needs to
 * expand that date into a full day range before it reaches the server.
 *
 * The expansion is anchored to the store's timezone, America/Mexico_City,
 * not to UTC: a naive `${date}T00:00:00.000Z` / `${date}T23:59:59.999Z`
 * would shift the boundary by the zone's offset (six hours) and silently
 * misfile the first/last six hours of sales into the wrong day. See
 * `docs/decisions/2026-07-31-report-date-range-timezone.md`.
 *
 * A value that is already a full ISO datetime (has a "T") is passed through
 * untouched, so callers that already have a precise instant are never
 * double-converted.
 */

import { DEFAULT_TIME_ZONE, zonedWallClockToIso } from '@equinox/shared';

// The zone math itself (offset resolution, wall-clock conversion) moved to
// @equinox/shared/timezone.ts when terminals gained per-device zones —
// one implementation, three consumers. This module keeps only the report
// screens' bare-date expansion contract.
export const REPORT_TIME_ZONE = DEFAULT_TIME_ZONE;
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseDateOnly(value: string): [number, number, number] {
  const parts = value.split('-').map(Number);
  const [y, mo, d] = parts;
  if (y === undefined || mo === undefined || d === undefined) {
    throw new Error(`not a YYYY-MM-DD date: ${value}`);
  }
  return [y, mo, d];
}

/**
 * Start of the given local day (00:00:00.000) as a UTC ISO instant.
 * A full ISO datetime passes through unchanged.
 */
export function startOfDayIso(value: string, timeZone: string = REPORT_TIME_ZONE): string {
  if (!DATE_ONLY_RE.test(value)) return value;
  const [y, mo, d] = parseDateOnly(value);
  return zonedWallClockToIso(y, mo, d, 0, 0, 0, 0, timeZone);
}

/**
 * End of the given local day (23:59:59.999) as a UTC ISO instant.
 * A full ISO datetime passes through unchanged.
 */
export function endOfDayIso(value: string, timeZone: string = REPORT_TIME_ZONE): string {
  if (!DATE_ONLY_RE.test(value)) return value;
  const [y, mo, d] = parseDateOnly(value);
  return zonedWallClockToIso(y, mo, d, 23, 59, 59, 999, timeZone);
}
